import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "uppercase bg-theme-gray-7 text-theme-gray-2 text-size-1 col-span-8 p-5"
}
const _hoisted_2 = {
  key: 4,
  class: "flex items-end col-span-7 md:col-span-1 p-5"
}

import { ref, onMounted, watch, computed } from 'vue'
import router from '@/router'
import NavTabs from '@/components/search/NavTabs.vue'
import DateField from '@/components/search/DateField.vue'
import TimeField from '@/components/search/TimeField.vue'
import LocationField from '@/components/search/LocationField.vue'
import DaysField from '@/components/search/DaysField.vue'
import TransferTypeField from '@/components/search/TransferTypeField.vue'
import ExtraTimeField from '@/components/search/ExtraTimeField.vue'
import ConfirmationModal from '@/widgets/ConfirmationModal.vue'
import { resetErrors, getHoursPerDay, getDiffInHours } from '@/utils/common'
import { saveUserSearch } from '@/services/search-service'
import LocationService from '@/services/location-service'
import type {
  LocationSchedule,
  DriveType,
  UserSearch,
  GoogleLocationType
} from '@/types'


export default /*@__PURE__*/_defineComponent({
  __name: 'Search',
  props: {
    layout: {},
    type: {},
    data: {},
    proceed: { type: Boolean },
    showMore: { type: Boolean }
  },
  setup(__props: any) {

const props = __props

/* Turn the incoming Search :type into internal drive values the form expects */
const TYPE_TO_DRIVE: Record<string, DriveType> = {
  airport_transfer: 'distance',   // Airport Transfer
  distance: 'distance',
  full_day_chauffeur: 'hourly',   // Full Day Chauffeur
  hourly: 'hourly'
}

const drive = ref<DriveType>(TYPE_TO_DRIVE[props.type ?? 'hourly'] ?? 'hourly')

/* Keep drive synced when parent changes the :type */
watch(() => props.type, (val) => {
  if (!val) return
  const mapped = TYPE_TO_DRIVE[val] ?? 'hourly'
  if (mapped !== drive.value) drive.value = mapped
})

/* Expose a single flag to NavTabs to show/hide “More” */
const showMoreTabs = computed(() => props.showMore !== false)

/* Form state & helpers */
const { getCoordinates } = LocationService()
const chaufferHours = ref<number | null>(getHoursPerDay())
const openConfirmModal = ref(false)
const extraTime = ref<number | null>(null)
const lastMinuteBookText = ref('This is a last minute booking. Text to be updated')

const pickUp = ref<LocationSchedule>({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } },
  date: null,
  time: ''
})

const dropOff = ref<LocationSchedule>({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } }
})

const errors = ref({
  pickUp: { location: false, date: false, time: false },
  dropOff: { location: false }
})

const proceedInitBooking = async () => {
  await setAddressCoordinates()
  resetErrors(errors.value)
  const isValid = validateSearchForm()

  if (!isValid) return

  if (props.layout === 'simple') {
    if (pickUp.value && pickUp.value.date) {
      const date = `${pickUp.value.date} ${pickUp.value.time}`
      const hours = getDiffInHours(date)
      if (hours < 48) {
        openConfirmModal.value = true
        return
      }
      saveAndGo()
    }
  } else {
    saveAndGo()
  }
}

const saveAndGo = () => {
  const userSearch: UserSearch = {
    type: drive.value,
    mode: 'one-way',
    pickUp: pickUp.value,
    dropOff: dropOff.value,
    hours: chaufferHours.value as number,
    extraTime: extraTime.value as number
  }
  saveUserSearch(userSearch)
  router.push({ name: 'BookingWizard', params: { step: 'choose-vehicle' } })
}

const handleDriveTypeChange = (value: string) => {
  drive.value = value as DriveType
}

const handleAddressSelected = (address: GoogleLocationType | null, type: string) => {
  const add = address ?? { id: '', name: '', coordinates: { lat: null, lng: null } }
  if (type === 'pickUp') {
    pickUp.value = { ...pickUp.value, location: add }
  } else if (type === 'dropOff') {
    dropOff.value = { ...dropOff.value, location: add }
  }
}

const handleDateChanged = (date: string) => { pickUp.value.date = date }
const handleTimeChanged = (time: string) => { pickUp.value.time = time }
const handleDaysChanged = (value: number) => { chaufferHours.value = value }
const handleExtraTimeChanged = (value: number) => { extraTime.value = value }
const handleConfirmClose = () => { openConfirmModal.value = false }
const askingConfirmed = async () => { router.push({ name: 'Planmytrip' }) }

const validateSearchForm = (): boolean => {
  let response = true

  if (pickUp.value.date === null) {
    errors.value.pickUp.date = true
    response = false
  }
  if (!pickUp.value.time) {
    errors.value.pickUp.time = true
    response = false
  }
  if (
    pickUp.value.location.id === '' ||
    pickUp.value.location.coordinates.lat === null ||
    pickUp.value.location.coordinates.lng === null
  ) {
    errors.value.pickUp.location = true
    response = false
  }
  if (drive.value === 'distance' && dropOff.value.location.id === '') {
    errors.value.dropOff.location = true
    response = false
  }
  return response
}

const setAddressCoordinates = async (): Promise<void> => {
  let response
  if (pickUp.value.location.id) {
    response = await getCoordinates(pickUp.value.location.id)
    if (response.success) {
      pickUp.value = {
        ...pickUp.value,
        location: { ...pickUp.value.location, coordinates: { ...response.data } }
      }
    }
  }
  if (drive.value === 'distance' && dropOff.value.location.id) {
    response = await getCoordinates(dropOff.value.location.id)
    if (response.success) {
      dropOff.value = {
        ...dropOff.value,
        location: { ...dropOff.value.location, coordinates: { ...response.data } }
      }
    }
  }
}

/* Emit pickUp/dropOff changes upward if needed */
watch(() => pickUp.value, (val) => { /* emit('pickUp', val) if parent uses it */ })
watch(() => dropOff.value, (val) => { /* emit('dropOff', val) if parent uses it */ })

/* Support an external "proceed" trigger if parent passes it (kept from your code) */
watch(() => props.proceed, (newVal) => {
  if (newVal) {
    proceedInitBooking()
    // emit('proceedInitiated', '1') // uncomment if your parent relies on this
  }
})

onMounted(() => { /* any mount logic */ })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass(["flex flex-col", _ctx.layout === 'simple' ? 'md:space-y-3' : ''])
    }, [
      _createVNode(NavTabs, {
        layout: _ctx.layout,
        driveType: drive.value,
        showMore: showMoreTabs.value,
        onDriveType: handleDriveTypeChange
      }, null, 8, ["layout", "driveType", "showMore"]),
      _createElementVNode("div", {
        class: _normalizeClass(["grid divide-x divide-y divide-theme-gray-7 gap-0 w-full bg-white", _ctx.layout === 'simple'
        ? 'grid-cols-7'
        : 'grid-cols-8 border-r border-b border-l border-theme-gray-7'])
      }, [
        (_ctx.layout === 'full')
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, " Ride Details "))
          : _createCommentVNode("", true),
        _createVNode(DateField, {
          layout: _ctx.layout,
          hasError: errors.value.pickUp.date,
          data: _ctx.data && _ctx.data.pickUp ? _ctx.data.pickUp.date : null,
          onDateChanged: handleDateChanged
        }, null, 8, ["layout", "hasError", "data"]),
        _createVNode(TimeField, {
          layout: _ctx.layout,
          hasError: errors.value.pickUp.time,
          data: _ctx.data && _ctx.data.pickUp ? _ctx.data.pickUp.time : null,
          onTimeChanged: handleTimeChanged
        }, null, 8, ["layout", "hasError", "data"]),
        _createVNode(LocationField, {
          layout: _ctx.layout,
          drive: drive.value,
          hasError: errors.value.pickUp.location,
          data: _ctx.data && _ctx.data.pickUp ? _ctx.data.pickUp : null,
          type: "pickUp",
          label: "Pick up location",
          onAddressSelected: handleAddressSelected
        }, null, 8, ["layout", "drive", "hasError", "data"]),
        (drive.value === 'distance')
          ? (_openBlock(), _createBlock(LocationField, {
              key: 1,
              layout: _ctx.layout,
              drive: drive.value,
              hasError: errors.value.dropOff.location,
              data: _ctx.data && _ctx.data.dropOff ? _ctx.data.dropOff : null,
              type: "dropOff",
              label: "Drop-off location",
              onAddressSelected: handleAddressSelected
            }, null, 8, ["layout", "drive", "hasError", "data"]))
          : (drive.value === 'hourly')
            ? (_openBlock(), _createBlock(DaysField, {
                key: 2,
                layout: _ctx.layout,
                data: _ctx.data ? _ctx.data.hours : null,
                onSelected: handleDaysChanged
              }, null, 8, ["layout", "data"]))
            : _createCommentVNode("", true),
        (_ctx.layout === 'full' && drive.value === 'distance')
          ? (_openBlock(), _createBlock(TransferTypeField, {
              key: 3,
              layout: _ctx.layout
            }, null, 8, ["layout"]))
          : _createCommentVNode("", true),
        (_ctx.layout === 'simple')
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", { class: "w-full" }, [
                _createElementVNode("button", {
                  onClick: proceedInitBooking,
                  class: "w-full uppercase text-size-2 py-3 px-4 text-center rounded-xl bg-theme-active border-2 border-theme-active text-white font-bold cursor-pointer hover:bg-white hover:text-theme-active transition duration-300 shadow-md active:scale-95"
                }, " Book Now ")
              ])
            ]))
          : _createCommentVNode("", true)
      ], 2)
    ], 2),
    _createVNode(ConfirmationModal, {
      show: openConfirmModal.value,
      confirmationText: lastMinuteBookText.value,
      onClosed: handleConfirmClose
    }, {
      buttons: _withCtx(() => [
        _createElementVNode("button", {
          onClick: askingConfirmed,
          type: "button",
          class: "inline-flex cursor-pointer w-full justify-center rounded-md bg-theme-active px-3 py-2 text-sm text-white shadow-sm xs:ml-3 xs:w-auto"
        }, " Request Quote "),
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (openConfirmModal.value = false)),
          type: "button",
          class: "mt-3 inline-flex w-full cursor-pointer justify-center rounded-md bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 xs:mt-0 xs:w-auto"
        }, " Cancel ")
      ]),
      _: 1
    }, 8, ["show", "confirmationText"])
  ], 64))
}
}

})